// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo } from 'react';

const fmt = (n) => n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const durataLabel = (mesi) => {
  const a = Math.floor(mesi / 12);
  const m = mesi % 12;
  if (a === 0) return `${m} mes${m === 1 ? 'e' : 'i'}`;
  if (m === 0) return `${a} ann${a === 1 ? 'o' : 'i'}`;
  return `${a} ann${a === 1 ? 'o' : 'i'} e ${m} mes${m === 1 ? 'e' : 'i'}`;
};

export default function FinanziamentoAuto() {
  const [inputs, setInputs] = useState({
    prezzo: 20000,
    anticipo: 3000,
    durata: 48,
    tan: 6.5,
    istruttoria: 300,
    assicurazione: 0,
  });

  const set = (k, v) => setInputs(prev => ({ ...prev, [k]: Number(v) }));

  const calc = useMemo(() => {
    const { prezzo, anticipo, durata, tan, istruttoria, assicurazione } = inputs;
    const importo = prezzo - anticipo;
    const r = tan / 100 / 12;
    let rata = 0;
    if (r === 0) {
      rata = importo / durata;
    } else {
      rata = importo * (r * Math.pow(1 + r, durata)) / (Math.pow(1 + r, durata) - 1);
    }
    const totalePagato = rata * durata + anticipo + istruttoria + assicurazione;
    const interessi = rata * durata - importo;
    const costoReale = totalePagato;
    const risparmioCache = interessi + istruttoria + assicurazione;
    // Deployment 14 — BUG 4: TAEG = TAN + costi annualizzati (formula educativa)
    // TAEG = TAN + (spese_istruttoria / durata_anni / capitale)*100 + (assicurazione_annua / capitale)*100
    const durataAnni = durata / 12;
    const taeg = importo > 0 && durataAnni > 0
      ? tan + (istruttoria / durataAnni / importo) * 100 + (assicurazione / durataAnni / importo) * 100
      : tan;
    return { rata, importo, totalePagato, interessi, costoReale, risparmioCache, taeg, durata };
  }, [inputs]);

  const taegColor = calc.taeg < 5 ? '#3D7A52' : calc.taeg < 10 ? '#E8903A' : '#C0392B';

  return (
    <div className="space-y-6" data-testid="finanziamento-auto-calc">
      {/* Educational box */}
      <div className="sapiens-note-box" data-testid="auto-intro">
        <p className="text-sm leading-relaxed">
          <strong>Il prezzo dell'auto non è il costo reale.</strong> Con un finanziamento paghi interessi che possono aggiungere migliaia di euro al totale. Questo calcolatore mostra il costo reale e lo confronta con l'acquisto in contanti.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* INPUTS */}
        <div className="space-y-5">
          <div>
            <label className="block text-sm text-[#888780] mb-1">Prezzo auto (€)</label>
            <input type="number" value={inputs.prezzo} onChange={e => set('prezzo', e.target.value)}
              className="w-full bg-[#252523] border border-white/10 rounded-lg px-4 py-2.5 text-[#E8E8E4] focus:border-[#C9A84C]/50 outline-none"
              data-testid="auto-prezzo" />
          </div>
          <div>
            <label className="block text-sm text-[#888780] mb-1">Anticipo versato (€)</label>
            <input type="number" value={inputs.anticipo} onChange={e => set('anticipo', e.target.value)}
              className="w-full bg-[#252523] border border-white/10 rounded-lg px-4 py-2.5 text-[#E8E8E4] focus:border-[#C9A84C]/50 outline-none"
              data-testid="auto-anticipo" />
          </div>
          <div data-testid="auto-durata-row">
            <label className="block text-sm text-[#888780] mb-1">
              Durata: <span className="text-[#E8E8E4]">{durataLabel(inputs.durata)}</span> ({inputs.durata} mesi)
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => set('durata', Math.max(12, inputs.durata - 6))}
                disabled={inputs.durata <= 12}
                className="w-10 h-10 rounded-lg bg-[#252523] border border-white/10 text-[#E8E8E4] hover:border-[#C9A84C]/50 disabled:opacity-30 disabled:cursor-not-allowed text-lg font-bold"
                data-testid="auto-durata-minus"
                aria-label="Diminuisci durata"
              >−</button>
              <input
                type="number"
                min={12}
                max={84}
                step={6}
                value={inputs.durata}
                onChange={e => {
                  const v = Math.min(84, Math.max(12, Number(e.target.value) || 12));
                  set('durata', v);
                }}
                className="flex-1 bg-[#252523] border border-white/10 rounded-lg px-4 py-2.5 text-[#E8E8E4] text-center focus:border-[#C9A84C]/50 outline-none"
                data-testid="auto-durata"
              />
              <button
                type="button"
                onClick={() => set('durata', Math.min(84, inputs.durata + 6))}
                disabled={inputs.durata >= 84}
                className="w-10 h-10 rounded-lg bg-[#252523] border border-white/10 text-[#E8E8E4] hover:border-[#C9A84C]/50 disabled:opacity-30 disabled:cursor-not-allowed text-lg font-bold"
                data-testid="auto-durata-plus"
                aria-label="Aumenta durata"
              >+</button>
            </div>
            <div className="flex justify-between text-xs text-[#888780] mt-1"><span>min 12 mesi</span><span>max 84 mesi (7 anni)</span></div>
          </div>
          <div>
            <label className="block text-sm text-[#888780] mb-1">TAN % annuo</label>
            <input type="number" step="0.1" value={inputs.tan} onChange={e => set('tan', e.target.value)}
              className="w-full bg-[#252523] border border-white/10 rounded-lg px-4 py-2.5 text-[#E8E8E4] focus:border-[#C9A84C]/50 outline-none"
              data-testid="auto-tan" />
            <p className="text-xs text-[#888780] mt-1">Tasso Annuo Nominale — base del finanziamento, senza spese</p>
          </div>
          <div>
            <label className="block text-sm text-[#888780] mb-1">Spese di istruttoria (€)</label>
            <input type="number" value={inputs.istruttoria} onChange={e => set('istruttoria', e.target.value)}
              className="w-full bg-[#252523] border border-white/10 rounded-lg px-4 py-2.5 text-[#E8E8E4] focus:border-[#C9A84C]/50 outline-none" />
          </div>
          <div>
            <label className="block text-sm text-[#888780] mb-1">Assicurazione obbligatoria (€)</label>
            <input type="number" value={inputs.assicurazione} onChange={e => set('assicurazione', e.target.value)}
              className="w-full bg-[#252523] border border-white/10 rounded-lg px-4 py-2.5 text-[#E8E8E4] focus:border-[#C9A84C]/50 outline-none" />
            <p className="text-xs sapiens-warning-text mt-1" data-testid="auto-assicurazione-note">
              <strong>L'assicurazione non è obbligatoria per il finanziamento auto</strong> (a differenza della cessione del quinto). Formula semplificata a scopo educativo — il TAEG bancario ufficiale è calcolato con metodo IRR.
            </p>
          </div>
        </div>

        {/* OUTPUT CARDS */}
        <div className="space-y-3">
          <div style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 12, padding: '16px 20px' }} data-testid="auto-rata">
            <div className="text-xs text-[#888780] mb-1">Rata mensile</div>
            <div className="text-3xl font-bold text-[#C9A84C]">€ {fmt(calc.rata)}</div>
          </div>
          <div style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '14px 18px' }} data-testid="auto-taeg">
            <div className="text-xs text-[#888780] mb-1">TAEG effettivo</div>
            <div className="text-2xl font-bold" style={{ color: taegColor }}>{calc.taeg.toFixed(2)}%</div>
            <div className="text-xs mt-1" style={{ color: taegColor }}>{calc.taeg < 5 ? 'Tasso contenuto' : calc.taeg < 10 ? 'Tasso moderato' : 'Tasso elevato'}</div>
          </div>
          <div style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '14px 18px' }} data-testid="auto-interessi">
            <div className="text-xs text-[#888780] mb-1">Totale interessi pagati</div>
            <div className="text-2xl font-bold text-[#E8E8E4]">€ {fmt(calc.interessi)}</div>
          </div>
          <div style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '14px 18px' }} data-testid="auto-costo-reale">
            <div className="text-xs text-[#888780] mb-1">Costo reale dell'auto</div>
            <div className="text-2xl font-bold text-[#E8E8E4]">€ {fmt(calc.costoReale)}</div>
          </div>
          <div style={{ background: 'rgba(232,144,58,0.1)', border: '1px solid rgba(232,144,58,0.25)', borderRadius: 10, padding: '14px 18px' }} data-testid="auto-risparmio">
            <div className="text-xs text-[#888780] mb-1">Risparmio con acquisto cash</div>
            <div className="text-2xl font-bold text-[#E8903A]">€ {fmt(calc.risparmioCache)}</div>
          </div>
        </div>
      </div>

      {/* COMPARISON TABLE */}
      <div data-testid="auto-comparison-table">
        <h3 className="text-sm font-medium text-[#E8E8E4] mb-3">Confronto finanziamento vs acquisto cash</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th className="text-left py-2 px-3 text-[#888780] font-normal"></th>
                <th className="text-right py-2 px-3 text-[#888780] font-normal">Finanziamento</th>
                <th className="text-right py-2 px-3 text-[#888780] font-normal">Acquisto cash</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Esborso iniziale', `€ ${fmt(inputs.anticipo)}`, `€ ${fmt(inputs.prezzo)}`],
                ['Rate mensili', `€ ${fmt(calc.rata)}/mese × ${calc.durata}`, '€ 0'],
                ['Totale pagato', `€ ${fmt(calc.totalePagato)}`, `€ ${fmt(inputs.prezzo)}`],
                ['Costo extra', `€ ${fmt(calc.risparmioCache)}`, '€ 0'],
              ].map(([label, fin, cash], i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                  <td className="py-2 px-3 text-[#888780]">{label}</td>
                  <td className="py-2 px-3 text-right text-[#E8E8E4]">{fin}</td>
                  <td className="py-2 px-3 text-right" style={{ color: '#3D7A52' }}>{cash}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* TAN vs TAEG educational box */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(201,168,76,0.25)' }} data-testid="tan-taeg-box">
        <div className="px-4 py-2 text-xs font-semibold text-[#C9A84C]" style={{ background: 'rgba(201,168,76,0.08)' }}>
          TAN vs TAEG — conosci la differenza prima di firmare
        </div>
        <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-white/[0.06]">
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" style={{ background: 'rgba(255,255,255,0.08)', color: '#888780' }}>TAN</div>
              <div>
                <p className="text-sm font-semibold text-[#E8E8E4] mb-1">Tasso Annuo Nominale</p>
                <p className="text-xs text-[#888780] leading-relaxed">È il tasso puro degli interessi. <strong className="text-[#E8E8E4]">Non include</strong> spese di istruttoria, assicurazioni o altri costi accessori. Usato per calcolare la rata mensile.</p>
                <div className="mt-2 px-2 py-1 rounded text-xs font-bold text-[#E8E8E4]" style={{ background: 'rgba(255,255,255,0.08)' }}>
                  TAN attuale: {inputs.tan}%
                </div>
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" style={{ background: `rgba(201,168,76,0.15)`, color: '#C9A84C' }}>TAEG</div>
              <div>
                <p className="text-sm font-semibold text-[#E8E8E4] mb-1">Tasso Annuo Effettivo Globale</p>
                <p className="text-xs text-[#888780] leading-relaxed">Il <strong className="text-[#E8E8E4]">costo reale</strong> del finanziamento: include TAN + tutte le spese (istruttoria, assicurazioni obbligatorie). La direttiva UE 2008/48/CE obbliga le banche a mostrarlo prima della firma.</p>
                <div className="mt-2 px-2 py-1 rounded text-xs font-bold" style={{ background: `rgba(201,168,76,0.15)`, color: taegColor }}>
                  TAEG effettivo: {calc.taeg.toFixed(2)}% {calc.taeg > inputs.tan * 1.2 ? '⚠ molto > TAN — molte spese!' : ''}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-2.5 text-xs text-[#888780]" style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          Regola pratica: confronta sempre il <strong className="text-[#C9A84C]">TAEG</strong> tra diverse offerte, non il TAN. Una rata bassa può nascondere un TAEG molto alto.
        </div>
      </div>

      <p className="text-xs text-[#888780] italic">Calcolo indicativo. I valori reali dipendono dalle condizioni specifiche del contratto. Richiedi sempre il SECCI (documento europeo standardizzato) prima di firmare.</p>
</div>
  );
}
